import jsPDF from 'jspdf';
import { Payment } from '../types/payment';

interface ReceiptData {
  payment: Payment | { payments: Payment[]; totalAmount: number };
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyLogo?: string;
}

export const generateReceiptPDF = (data: ReceiptData) => {
  const {
    payment,
    companyName = 'Your Company Name',
    companyAddress = 'Your Company Address',
    companyPhone = '+123 456 7890',
    companyEmail = 'info@yourcompany.com',
    companyLogo
  } = data;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  // Check if this is a multi-payment response or single payment
  const isMultiPayment = 'payments' in payment && Array.isArray((payment as any).payments);
  const payments: Payment[] = isMultiPayment
    ? (payment as { payments: Payment[]; totalAmount: number }).payments
    : [payment as Payment];
  const firstPayment = payments[0];
  const totalAmount = isMultiPayment
    ? (payment as { payments: Payment[]; totalAmount: number }).totalAmount
    : (payment as Payment).amount;

  // Helper function to convert hex color to RGB
  const hexToRgb = (hex: string): [number, number, number] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
      : [0, 0, 0];
  };

  // Check if payment has invoice with template - use template colors, otherwise use defaults
  const invoiceTemplate = firstPayment.invoice?.template;
  const primaryColor: [number, number, number] = invoiceTemplate?.colorScheme?.primary
    ? hexToRgb(invoiceTemplate.colorScheme.primary)
    : [22, 163, 74]; // Green
  const darkGray: [number, number, number] = invoiceTemplate?.colorScheme?.text
    ? hexToRgb(invoiceTemplate.colorScheme.text)
    : [52, 73, 94];
  const lightGray: [number, number, number] = [189, 195, 199];

  // Use template font if available
  const fontFamily = invoiceTemplate?.fontFamily || 'helvetica';

  // Helper functions
  const formatCurrency = (amount: number) => `BD ${amount.toFixed(3)}`;
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const formatDateTime = (date: Date) => {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRecipient = () => {
    if (firstPayment.customer) return firstPayment.customer;
    if (firstPayment.vendor) return firstPayment.vendor;
    return null;
  };

  const recipient = getRecipient();
  const isBillPayment = firstPayment.vendor && firstPayment.bill;

  // Header with Company Logo - prioritize template logo if available
  const templateLogoBase64 = invoiceTemplate?.logoBase64;
  const templateLogo = invoiceTemplate?.templateLogo;
  const logoToUse = templateLogoBase64 || templateLogo || companyLogo;
  const displayCompanyName = invoiceTemplate?.templateCompanyName || companyName;

  if (templateLogoBase64) {
    // Use base64 logo from template (best quality, no CORS issues)
    try {
      doc.addImage(templateLogoBase64, 'PNG', 15, 12, 25, 25);
    } catch (error) {
      console.error('Error loading template base64 logo:', error);
      // Fallback to circle with initials
      doc.setFillColor(...primaryColor);
      doc.circle(25, 25, 12, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont(fontFamily.toLowerCase(), 'bold');
      const initials = displayCompanyName.split(' ').map(word => word[0]).join('').toUpperCase();
      doc.text(initials, 25, 28, { align: 'center' });
    }
  } else if (logoToUse) {
    // Use URL logo (template or company)
    try {
      const logoUrl = logoToUse.startsWith('http')
        ? logoToUse
        : `${window.location.origin.replace(':3000', ':5002')}/${logoToUse}`;
      doc.addImage(logoUrl, 'PNG', 15, 12, 25, 25);
    } catch (error) {
      console.error('Error loading logo:', error);
      // Fallback to circle with initials
      doc.setFillColor(...primaryColor);
      doc.circle(25, 25, 12, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont(fontFamily.toLowerCase(), 'bold');
      const initials = displayCompanyName.split(' ').map(word => word[0]).join('').toUpperCase();
      doc.text(initials, 25, 28, { align: 'center' });
    }
  } else {
    // Default circle with initials if no logo
    doc.setFillColor(...primaryColor);
    doc.circle(25, 25, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont(fontFamily.toLowerCase(), 'bold');
    const initials = displayCompanyName.split(' ').map(word => word[0]).join('').toUpperCase();
    doc.text(initials, 25, 28, { align: 'center' });
  }

  // Company Info
  doc.setTextColor(...darkGray);
  doc.setFontSize(16);
  doc.setFont(fontFamily.toLowerCase(), 'bold');

  // Company name with proper wrapping - use template name if available
  const maxCompanyNameWidth = pageWidth - 60;
  const companyNameLines = doc.splitTextToSize(displayCompanyName, maxCompanyNameWidth);
  doc.text(companyNameLines, 45, 18);

  let yPos2 = 18 + (companyNameLines.length * 5);
  doc.setFontSize(9);
  doc.setFont(fontFamily.toLowerCase(), 'normal');

  if (companyAddress) {
    const addressLines = doc.splitTextToSize(companyAddress, maxCompanyNameWidth);
    doc.text(addressLines, 45, yPos2);
    yPos2 += addressLines.length * 4;
  }

  if (companyPhone) {
    doc.text(companyPhone, 45, yPos2);
    yPos2 += 4;
  }

  if (companyEmail) {
    doc.setTextColor(41, 128, 185);
    doc.text(companyEmail, 45, yPos2);
    doc.setTextColor(...darkGray);
  }

  yPos = 55;

  // PAYMENT RECEIPT Title
  doc.setTextColor(...darkGray);
  doc.setFontSize(24);
  doc.setFont(fontFamily.toLowerCase(), 'bold');
  doc.text('PAYMENT RECEIPT', pageWidth / 2, yPos, { align: 'center' });

  yPos = 70;

  // Payment Details Box
  doc.setDrawColor(...lightGray);
  doc.setLineWidth(0.5);
  doc.rect(15, yPos, pageWidth - 30, 35);

  // Grid layout for details
  const col1X = 20;
  const col2X = 70;
  const col3X = 120;

  yPos += 10;
  doc.setFontSize(9);
  doc.setTextColor(...darkGray);
  doc.setFont(fontFamily.toLowerCase(), 'bold');

  // Row 1
  doc.text('Payment Date:', col1X, yPos);
  doc.setFont(fontFamily.toLowerCase(), 'normal');
  doc.text(formatDate(firstPayment.paymentDate), col1X, yPos + 5);

  doc.setFont(fontFamily.toLowerCase(), 'bold');
  doc.text('Transaction #:', col2X, yPos);
  doc.setFont(fontFamily.toLowerCase(), 'normal');
  doc.text(firstPayment.transactionNumber, col2X, yPos + 5);

  if (firstPayment.reference) {
    doc.setFont(fontFamily.toLowerCase(), 'bold');
    doc.text('Reference:', col3X, yPos);
    doc.setFont(fontFamily.toLowerCase(), 'normal');
    const refText = doc.splitTextToSize(firstPayment.reference, 35);
    doc.text(refText, col3X, yPos + 5);
  }

  yPos += 15;

  // Row 2
  doc.setFont(fontFamily.toLowerCase(), 'bold');
  doc.text('Payment Mode:', col1X, yPos);
  doc.setFont(fontFamily.toLowerCase(), 'normal');
  const paymentMethod = (firstPayment.paymentMethod || 'cash').replace('_', ' ');
  doc.text(paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1), col1X, yPos + 5);

  if (isMultiPayment && payments.length > 1) {
    doc.setFont(fontFamily.toLowerCase(), 'bold');
    doc.text('Invoices Paid:', col2X, yPos);
    doc.setFont(fontFamily.toLowerCase(), 'normal');
    doc.setTextColor(41, 128, 185);
    doc.text(`${payments.length} Invoices`, col2X, yPos + 5);
    doc.setTextColor(...darkGray);
  } else if (firstPayment.invoice || firstPayment.bill) {
    const label = firstPayment.invoice ? 'Invoice #:' : 'Bill #:';
    const number = firstPayment.invoice
      ? firstPayment.invoice.invoiceNumber
      : firstPayment.bill?.billNumber || '';
    doc.setFont(fontFamily.toLowerCase(), 'bold');
    doc.text(label, col2X, yPos);
    doc.setFont(fontFamily.toLowerCase(), 'normal');
    doc.setTextColor(41, 128, 185);
    doc.text(number, col2X, yPos + 5);
    doc.setTextColor(...darkGray);
  }

  yPos = 120;

  // Amount Box - Highlighted
  const amountBoxWidth = 60;
  const amountBoxX = pageWidth - 15 - amountBoxWidth;

  doc.setFillColor(...primaryColor);
  doc.roundedRect(amountBoxX, yPos, amountBoxWidth, 25, 3, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont(fontFamily.toLowerCase(), 'normal');
  doc.text(isBillPayment ? 'Amount Paid' : 'Amount Received', amountBoxX + 30, yPos + 8, {
    align: 'center'
  });

  doc.setFontSize(18);
  doc.setFont(fontFamily.toLowerCase(), 'bold');
  doc.text(formatCurrency(totalAmount), amountBoxX + 30, yPos + 19, { align: 'center' });

  yPos = 155;

  // Recipient Section
  if (recipient) {
    doc.setTextColor(...darkGray);
    doc.setFontSize(12);
    doc.setFont(fontFamily.toLowerCase(), 'bold');
    doc.text(isBillPayment ? 'Payment To:' : 'Received From:', 15, yPos);

    yPos += 7;
    doc.setFillColor(250, 250, 250);
    doc.roundedRect(15, yPos, pageWidth - 30, 18, 2, 2, 'F');

    yPos += 8;
    doc.setFontSize(11);
    doc.setFont(fontFamily.toLowerCase(), 'bold');
    doc.text(recipient.name, 20, yPos);

    if (recipient.email) {
      yPos += 6;
      doc.setFontSize(9);
      doc.setFont(fontFamily.toLowerCase(), 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(recipient.email, 20, yPos);
      doc.setTextColor(...darkGray);
    }

    yPos += 12;
  }

  // Payment For Section - Table of invoices/bills
  if (payments.some(p => p.invoice || p.bill)) {
    yPos += 5;
    doc.setFontSize(12);
    doc.setFont(fontFamily.toLowerCase(), 'bold');
    doc.text('Payment For:', 15, yPos);

    yPos += 7;

    // Table Header
    const tableStartY = yPos;
    const col1Width = 45;
    const col2Width = 40;
    const col3Width = 40;

    doc.setFillColor(240, 240, 240);
    doc.rect(15, tableStartY, pageWidth - 30, 10, 'F');

    doc.setDrawColor(...lightGray);
    doc.rect(15, tableStartY, pageWidth - 30, 10);

    doc.setFontSize(9);
    doc.setFont(fontFamily.toLowerCase(), 'bold');
    doc.setTextColor(...darkGray);

    let colX = 18;
    doc.text(firstPayment.invoice ? 'Invoice Number' : 'Bill Number', colX, tableStartY + 6.5);
    colX += col1Width;
    doc.text(firstPayment.invoice ? 'Invoice Date' : 'Bill Date', colX, tableStartY + 6.5);
    colX += col2Width;
    doc.text(firstPayment.invoice ? 'Invoice Amount' : 'Bill Amount', colX, tableStartY + 6.5);
    colX += col3Width;
    doc.text('Payment', colX, tableStartY + 6.5);

    yPos = tableStartY + 10;

    // Table Rows
    payments.forEach((paymentItem, index) => {
      if (paymentItem.invoice || paymentItem.bill) {
        const rowHeight = 10;

        // Alternate row background
        if (index % 2 === 0) {
          doc.setFillColor(252, 252, 252);
          doc.rect(15, yPos, pageWidth - 30, rowHeight, 'F');
        }

        doc.setDrawColor(...lightGray);
        doc.rect(15, yPos, pageWidth - 30, rowHeight);

        doc.setFont(fontFamily.toLowerCase(), 'normal');
        doc.setFontSize(9);
        doc.setTextColor(41, 128, 185);

        colX = 18;
        const number = paymentItem.invoice
          ? paymentItem.invoice.invoiceNumber
          : paymentItem.bill?.billNumber || '';
        doc.text(number, colX, yPos + 6.5);

        doc.setTextColor(...darkGray);
        colX += col1Width;
        doc.text(formatDate(paymentItem.paymentDate), colX, yPos + 6.5);

        colX += col2Width;
        const total = paymentItem.invoice
          ? paymentItem.invoice.total
          : paymentItem.bill?.total || 0;
        doc.text(formatCurrency(total), colX, yPos + 6.5);

        colX += col3Width;
        doc.setFont(fontFamily.toLowerCase(), 'bold');
        doc.text(formatCurrency(paymentItem.amount), colX, yPos + 6.5);

        yPos += rowHeight;
      }
    });

    // Total Row
    if (isMultiPayment) {
      doc.setFillColor(240, 240, 240);
      doc.rect(15, yPos, pageWidth - 30, 10, 'F');
      doc.setDrawColor(...lightGray);
      doc.rect(15, yPos, pageWidth - 30, 10);

      doc.setFont(fontFamily.toLowerCase(), 'bold');
      doc.setFontSize(10);
      doc.text('Total Payment', 18, yPos + 6.5);

      colX = 18 + col1Width + col2Width + col3Width;
      doc.text(formatCurrency(totalAmount), colX, yPos + 6.5);

      yPos += 10;
    }

    yPos += 5;
  }

  // Notes Section
  if (firstPayment.notes) {
    yPos += 5;
    doc.setFontSize(11);
    doc.setFont(fontFamily.toLowerCase(), 'bold');
    doc.setTextColor(...darkGray);
    doc.text('Notes:', 15, yPos);

    yPos += 5;
    doc.setFillColor(250, 250, 250);
    const notesLines = doc.splitTextToSize(firstPayment.notes, pageWidth - 34);
    const notesHeight = notesLines.length * 5 + 6;
    doc.roundedRect(15, yPos, pageWidth - 30, notesHeight, 2, 2, 'F');

    yPos += 5;
    doc.setFontSize(9);
    doc.setFont(fontFamily.toLowerCase(), 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(notesLines, 18, yPos);

    yPos += notesHeight;
  }

  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 25;
  doc.setDrawColor(...lightGray);
  doc.setLineWidth(0.5);
  doc.line(15, footerY, pageWidth - 15, footerY);

  doc.setFontSize(8);
  doc.setTextColor(...lightGray);
  doc.setFont(fontFamily.toLowerCase(), 'italic');

  const generatedText = `Generated on ${formatDateTime(new Date())}`;
  doc.text(generatedText, pageWidth / 2, footerY + 6, { align: 'center' });

  doc.text(`Transaction ID: ${firstPayment.id}`, pageWidth / 2, footerY + 11, {
    align: 'center'
  });

  if (isMultiPayment) {
    doc.setFontSize(7);
    doc.text(
      `Multi-payment transaction covering ${payments.length} invoices`,
      pageWidth / 2,
      footerY + 15,
      { align: 'center' }
    );
  }

  // Save the PDF
  const recipientName = recipient ? recipient.name.replace(/\s+/g, '_') : 'Payment';
  const fileName = `Receipt_${firstPayment.transactionNumber}_${recipientName}.pdf`;
  doc.save(fileName);
};
